// ProfilePage.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { getFirestore, collection, getDocs } from "firebase/firestore";
import { useAnalysis } from "../contexts/AnalysisContext";
import ShortField from "../components/field/ShortField";
import { useTranslation } from "react-i18next";
import { 
  FaChartLine, 
  FaMapMarkerAlt, 
  FaCog, 
  FaArrowRight, 
  FaSeedling, 
  FaImage,
  FaHistory,
  FaUser,
  FaEnvelope,
  FaKey,
  FaChartBar,
  FaChevronRight,
  FaTools // Maintenance icon
} from "react-icons/fa";
import { checkPageStatus } from "../utilityhook/pagecheck"; // ✅ import utility

const ProfilePage = () => {
  const navigate = useNavigate();
  const auth = getAuth();
  const user = auth.currentUser;
  const { t } = useTranslation();
  const [email, setEmail] = useState(user ? user.email : "");
  const [fields, setFields] = useState([]);
  const { loginTime, changePassTime, addFieldTime, analysisTime } = useAnalysis();
  const [isActive, setIsActive] = useState(true); // ✅ track maintenance status

  useEffect(() => {
    const fetchPageStatus = async () => {
      const active = await checkPageStatus("1-profile"); // ✅ wait for backend or localStorage
      setIsActive(active);
    };
    fetchPageStatus();
  }, []);

  useEffect(() => {
    if (user && isActive) {
      setEmail(user.email);
      fetchFields();
    }
  }, [user, isActive]);

  const fetchFields = async () => {
    try {
      const db = getFirestore();
      const fieldsCollectionRef = collection(db, `users/${user.uid}/fields`);
      const querySnapshot = await getDocs(fieldsCollectionRef);
      const fieldsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setFields(fieldsData);
    } catch (error) {
      console.error("Error fetching fields:", error);
    }
  };

  const handleViewAnalysis = () => navigate("/map-selector");
  const handleDashboard = () => navigate("/map-selector");
  const handleSettings = () => navigate("/settings");

  const activityItems = [
    {
      icon: <FaSeedling className="text-green-500" />,
      time: addFieldTime,
      text: t("Add the field"),
      color: "bg-green-50 border-green-200"
    },
    {
      icon: <FaUser className="text-blue-500" />,
      time: loginTime,
      text: t("User Login"),
      color: "bg-blue-50 border-blue-200"
    },
    {
      icon: <FaKey className="text-orange-500" />,
      time: changePassTime,
      text: t("User changed the password"),
      color: "bg-orange-50 border-orange-200"
    },
    {
      icon: <FaChartBar className="text-purple-500" />,
      time: analysisTime,
      text: t("Recently checked the analysis"),
      color: "bg-purple-50 border-purple-200"
    }
  ];

  // 🚧 Under Maintenance Overlay (only visible when inactive)
  if (!isActive) {
    return (
      <div className="p-4 w-full lg:w-[93%] 2xl:w-[95%] absolute right-0 min-h-screen bg-gray-50">
        <div className="h-screen bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300 bg-opacity-90 flex flex-col items-center justify-center text-center z-[9999] backdrop-blur-sm rounded-2xl">
          <FaTools className="text-5xl text-green-500 mb-4 animate-bounce" />
          <h1 className="text-3xl md:text-5xl font-semibold text-gray-600 mb-2">
            Page Under Maintenance
          </h1>
          <p className="text-gray-500 text-lg max-w-md">
            We're currently enhancing this feature for better accuracy and
            user experience. Please check back soon!
          </p>
          <div className="mt-6">
            <div className="w-16 h-16 border-4 border-gray-600 border-t-green-500 rounded-full animate-spin mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6 w-full lg:w-[93%] 2xl:w-[95%] absolute right-0 min-h-screen bg-gray-50">
      {/* Welcome Section - Sticky only on medium screens and above */}
      <div className="lg:sticky lg:top-4 z-40">
        <div className="bg-gradient-to-r from-green-500 to-green-600 w-full p-6 rounded-2xl flex flex-col sm:flex-row gap-6 items-center justify-between text-white shadow-lg">
          <div className="flex-1">
            <h2 className="text-3xl font-bold mb-2">{t("Welcome back")} 👋</h2>
            <h3 className="text-xl font-semibold mb-4">
              {user?.displayName || t("User")}
            </h3>
            <p className="text-green-100 mb-6 text-lg">
              {t("Every day on the farm is a chapter in the story of hard work, dedication, and the unwavering hope for a bountiful harvest.")}
            </p>
            <button
              className="bg-white text-green-600 w-60 px-6 py-3 rounded-xl font-semibold hover:bg-green-50 transition-all duration-300 shadow-md flex items-center justify-center gap-2"
              onClick={handleDashboard}
            >
              {t("Get Started")} <FaArrowRight />
            </button>
          </div>
          <div className="relative">
            <img
              src={require("../assets/profile.jpg")}
              alt="Illustration"
              className="w-32 h-32 rounded-full border-4 border-white shadow-2xl"
            />
            <div className="absolute -bottom-2 -right-2 bg-green-400 w-8 h-8 rounded-full border-4 border-white flex items-center justify-center">
              <div className="w-2 h-2 bg-white rounded-full"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section - Normal scroll on mobile, scrolls under sticky header on desktop */}
      <div className="space-y-6">
        {/* Quick Actions Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-blue-100 rounded-xl">
                <FaChartLine className="text-blue-600 text-xl" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-800">{t("Field Analysis")}</h3>
                <p className="text-sm text-gray-600">{t("Advanced insights")}</p>
              </div>
            </div>
            <button
              className="w-full bg-blue-500 text-white px-4 py-3 rounded-xl font-medium hover:bg-blue-600 transition-all duration-300 flex items-center justify-center gap-2"
              onClick={handleViewAnalysis}
            >
              {t("View Analysis")} <FaArrowRight className="text-sm" />
            </button>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-green-100 rounded-xl">
                <FaMapMarkerAlt className="text-green-600 text-xl" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-800">{t("Field Management")}</h3>
                <p className="text-sm text-gray-600">{t("Add & manage fields")}</p>
              </div>
            </div>
            <button
              className="w-full bg-green-500 text-white px-4 py-3 rounded-xl font-medium hover:bg-green-600 transition-all duration-300 flex items-center justify-center gap-2"
              onClick={handleDashboard}
            >
              {t("Manage Fields")} <FaArrowRight className="text-sm" />
            </button>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-orange-100 rounded-xl">
                <FaCog className="text-orange-600 text-xl" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-800">{t("Settings")}</h3>
                <p className="text-sm text-gray-600">{t("Preferences & account")}</p>
              </div>
            </div>
            <button
              className="w-full bg-orange-500 text-white px-4 py-3 rounded-xl font-medium hover:bg-orange-600 transition-all duration-300 flex items-center justify-center gap-2"
              onClick={handleSettings}
            >
              {t("Settings")} <FaArrowRight className="text-sm" />
            </button>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* My Fields Section */}
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <FaMapMarkerAlt className="text-blue-500" />
                {t("My Fields")}
              </h3>
              <span className="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-sm font-medium">
                {fields.length} {t("fields")}
              </span>
            </div>
            <div className="h-[400px] overflow-hidden">
              <ShortField />
            </div>
          </div>

          {/* Activity Section */}
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <FaHistory className="text-purple-500" />
                {t("Recent Activity")}
              </h3>
              <button className="text-blue-500 hover:text-blue-600 text-sm font-medium flex items-center gap-1">
                {t("View All")} <FaChevronRight className="text-xs" />
              </button>
            </div>

            {/* User Info Card */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-xl mb-6 border border-gray-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FaEnvelope className="text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t("Email")}</p>
                  <p className="font-medium text-gray-800">{email}</p>
                </div>
              </div>
            </div>

            {/* Activity Timeline */}
            <div className="space-y-4 max-h-[280px] overflow-y-auto scrollbar-hide">
              {activityItems.map((item, index) => (
                <div 
                  key={index} 
                  className={`p-4 rounded-xl border-l-4 ${item.color} transition-all duration-300 hover:scale-[1.02] hover:shadow-sm`}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-white shadow-sm">
                      {item.icon}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-600">{item.text}</p>
                      <p className="text-xs text-gray-500 mt-1">{item.time || t("No activity recorded")}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Features Highlight */}
        <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 rounded-2xl text-white">
          <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
            <FaSeedling />
            {t("Premium Features")}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-400 rounded-lg">
                <FaImage className="text-white" />
              </div>
              <div>
                <p className="font-semibold">{t("Calculated Areas")}</p>
                <p className="text-green-100 text-sm">{t("Precise field measurements")}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-400 rounded-lg">
                <FaChartLine className="text-white" />
              </div>
              <div>
                <p className="font-semibold">{t("Advanced Analytics")}</p>
                <p className="text-green-100 text-sm">{t("Deep insights and reporting")}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;